// utils/orderTotals.js
// ─────────────────────────────────────────────────────────────────────────────
// ONE definition of what an order costs and why.
//
// The same `itemsPrice / deliveryFee ?? shippingCost / totalPrice` fallback was
// hand-rolled in four confirmation pages, each slightly differently. It also
// only ever produced a single grand total — which is why a 3,000 UGX drink
// showed as "USh 8,000" with no explanation of the other 5,000.
//
// Careful: `deliveryFee` exists at TWO levels on the schema. The root one
// (Orders.js) is authoritative. `order.vendorOrders[].shippingCost` carries an
// `alias: "deliveryFee"` and is a DIFFERENT, per-vendor number — never read it
// as the order's delivery fee.
// ─────────────────────────────────────────────────────────────────────────────

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {object} order
 * @returns {{
 *   itemsSubtotal:number, deliveryFee:number, distanceKm:number|null,
 *   overageFee:number, overageKm:number, processingFee:number,
 *   processingLabel:string, total:number, hasBreakdown:boolean
 * }}
 */
export const orderTotals = (order) => {
  const itemsFromLines = Array.isArray(order?.orderItems)
    ? order.orderItems.reduce((sum, i) => sum + num(i?.price) * num(i?.qty ?? i?.quantity), 0)
    : 0;

  // Prefer the persisted subtotal; fall back to summing the lines so the
  // breakdown still renders on legacy orders that predate itemsPrice.
  const itemsSubtotal = num(order?.itemsPrice) || itemsFromLines;
  const deliveryFee = num(order?.deliveryFee ?? order?.shippingCost);
  const overageFee = num(order?.deliveryOverage?.extraFee);
  const overageKm = num(order?.deliveryOverage?.billableKm);

  /* The payment processing fee, when Lusuku is passing the rail charge on rather
     than absorbing it. It is CONTAINED IN totalPrice like the delivery fee, so it
     has to be part of the breakdown or the parts stop explaining the whole and
     `hasBreakdown` goes false on every card and mobile-money order. The label is
     the one stored on the order, so a receipt says what the customer was shown at
     the time and not whatever the setting says today. */
  const processingFee = num(order?.paymentProcessingFee);
  const processingLabel =
    (order?.paymentRail?.feeLabel || "").trim() || "Payment processing fee";

  const distanceRaw = Number(order?.distanceKm);
  const distanceKm = Number.isFinite(distanceRaw) && distanceRaw > 0 ? distanceRaw : null;

  const total = num(order?.totalPrice) || itemsSubtotal + deliveryFee + processingFee;

  return {
    itemsSubtotal,
    deliveryFee,
    distanceKm,
    overageFee,
    overageKm,
    processingFee,
    processingLabel,
    total,
    // Only claim a breakdown when the parts actually explain the whole; a
    // half-explained total is worse than an unexplained one.
    hasBreakdown:
      itemsSubtotal > 0 &&
      Math.abs(itemsSubtotal + deliveryFee + processingFee - total) <= 1,
  };
};

export default orderTotals;
